import { observer } from "mobx-react-lite";
import { EasyAvatar } from "fin-web-components";
import { runInAction } from "mobx";
import { useCallback, useEffect, useRef, useState } from "react";
import { App, Button, DatePicker, Input, Modal, Radio, Tooltip } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import {
  CameraOutlined,
  CopyOutlined,
  ManOutlined,
  UserOutlined,
  WomanOutlined,
} from "@ant-design/icons";
import { globalModel } from "../global/GlobalModel";
import { UserProfileApi } from "./UserProfileApi";
import { randomNickname } from "./randomNickname";
import { USER_PROFILE_NICKNAME_MAX_LENGTH } from "./Config";
import { avatarDisplayWord } from "../utils/avatarDisplayWord";
import { resolveAppMediaUrl, toPersistableAvatarUrl } from "../utils/resolveAppMediaUrl";
import "./ProfileCompleteModal.css";

/** 打开生日日历时默认定位到该日期（仅影响面板，不代替用户选择） */
const BIRTHDAY_PANEL_DEFAULT = dayjs("1990-12-20");

export const ProfileCompleteModal = observer(() => {
  const { message } = App.useApp();
  const forced = globalModel.profileSetupRequired;
  const optionalOpen = globalModel.profileEditorOpen;
  const open = forced || optionalOpen;
  const allowDismiss = !forced && optionalOpen;
  const [nickname, setNickname] = useState("");
  /** 服务端头像地址，保存资料时使用 */
  const [avatarUrl, setAvatarUrl] = useState("");
  /** 选图后的本地 blob 预览，上传成功保留、失败撤销 */
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [gender, setGender] = useState<"U" | "M" | "F">("U");
  const [birthday, setBirthday] = useState<Dayjs | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  /** 最近一次上传成功返回的服务端地址，避免 state 未及时同步导致保存时丢失 */
  const uploadedServerAvatarRef = useRef<string | null>(null);

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setAvatarPreviewUrl(null);
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      revokePreview();
      return;
    }
    revokePreview();
    uploadedServerAvatarRef.current = null;
    if (forced) {
      setNickname(randomNickname().slice(0, USER_PROFILE_NICKNAME_MAX_LENGTH));
      setAvatarUrl("");
      setGender("U");
      setBirthday(null);
      return;
    }
    if (optionalOpen && globalModel.currentUser) {
      const u = globalModel.currentUser;
      setNickname((u.name ?? "").trim() || "");
      setAvatarUrl(toPersistableAvatarUrl(u.avatarUrl));
      setGender("U");
      const b = u.birthday?.trim();
      if (b) {
        const d = dayjs(b);
        setBirthday(d.isValid() ? d : null);
      } else {
        setBirthday(null);
      }
    }
  }, [open, forced, optionalOpen, revokePreview]);

  const runPickFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        message.warning("请选择图片");
        return;
      }
      revokePreview();
      const localUrl = URL.createObjectURL(file);
      previewUrlRef.current = localUrl;
      setAvatarPreviewUrl(localUrl);

      setUploading(true);
      try {
        const url = await UserProfileApi.uploadAvatarFromCompressedFile(file);
        if (!url) {
          revokePreview();
          uploadedServerAvatarRef.current = null;
          message.error("上传失败，请重试");
          return;
        }
        uploadedServerAvatarRef.current = url;
        setAvatarUrl(toPersistableAvatarUrl(url));
        message.success("已上传");
      } catch {
        revokePreview();
        uploadedServerAvatarRef.current = null;
        message.error("上传失败，请重试");
      } finally {
        setUploading(false);
      }
    },
    [message, revokePreview]
  );

  const letter = avatarDisplayWord(nickname) || "·";
  const displayAvatarUrl =
    avatarPreviewUrl || resolveAppMediaUrl(avatarUrl) || undefined;
  const userId = globalModel.currentUser?.id?.trim() ?? "";

  const onCopyUserId = useCallback(() => {
    if (!userId) {
      return;
    }
    void navigator.clipboard.writeText(userId).then(
      () => {
        message.success("已复制用户ID");
      },
      () => {
        message.error("复制失败，请重试");
      }
    );
  }, [message, userId]);

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      e.target.value = "";
      if (f) {
        void runPickFile(f);
      }
    },
    [runPickFile]
  );

  const onSubmit = useCallback(async () => {
    const n = nickname.trim();
    if (!n) {
      message.warning("请填写昵称");
      return;
    }
    if (n.length > USER_PROFILE_NICKNAME_MAX_LENGTH) {
      message.warning(`昵称最多 ${USER_PROFILE_NICKNAME_MAX_LENGTH} 字`);
      return;
    }
    const rawAv = (uploadedServerAvatarRef.current ?? avatarUrl).trim();
    const persistAv = toPersistableAvatarUrl(rawAv);
    if (avatarPreviewUrl && !persistAv) {
      message.warning("请等待头像上传完成");
      return;
    }
    if (persistAv.length > 1024) {
      message.warning("头像信息异常，请重传");
      return;
    }
    if (birthday) {
      if (birthday.isAfter(dayjs(), "day")) {
        message.warning("生日不能晚于今天");
        return;
      }
      if (birthday.isBefore(dayjs("1900-01-01"), "day")) {
        message.warning("生日不早于 1900-01-01");
        return;
      }
    }
    setSubmitting(true);
    try {
      const ok = await UserProfileApi.updateProfile({
        nickname: n,
        avatarUrl: persistAv,
        gender,
        birthday: birthday ? birthday.format("YYYY-MM-DD") : "",
      });
      if (!ok) {
        message.error("保存失败，请重试");
        return;
      }
      const bdayYmd = birthday ? birthday.format("YYYY-MM-DD") : undefined;
      const resolvedAvatar = persistAv
        ? resolveAppMediaUrl(persistAv)
        : undefined;
      runInAction(() => {
        const cur = globalModel.currentUser;
        if (cur) {
          globalModel.currentUser = {
            ...cur,
            name: n,
            avatarUrl: resolvedAvatar ?? resolveAppMediaUrl(cur.avatarUrl),
            birthday: bdayYmd,
          };
        }
        globalModel.profileSetupRequired = false;
        globalModel.profileEditorOpen = false;
      });
      void globalModel.refreshUserFromServer().then(() => {
        runInAction(() => {
          const cur = globalModel.currentUser;
          if (!cur || !resolvedAvatar) {
            return;
          }
          if (!resolveAppMediaUrl(cur.avatarUrl)) {
            globalModel.currentUser = {
              ...cur,
              avatarUrl: resolvedAvatar,
            };
          }
        });
      });
      uploadedServerAvatarRef.current = null;
      message.success("已保存");
    } finally {
      setSubmitting(false);
    }
  }, [avatarPreviewUrl, avatarUrl, birthday, gender, message, nickname]);

  return (
    <Modal
      className="profile-complete-modal"
      wrapClassName="profile-complete-wrap"
      open={open}
      closable={allowDismiss}
      maskClosable={allowDismiss}
      keyboard={allowDismiss}
      onCancel={() => {
        runInAction(() => {
          globalModel.closeProfileEditor();
        });
      }}
      centered
      zIndex={3000}
      width={400}
      footer={null}
      title={null}
      destroyOnClose
    >
      <div className="profile-complete-body">
        <h2 className="profile-complete-title">个人资料</h2>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="profile-complete-file"
          onChange={onFileChange}
          tabIndex={-1}
        />
        <div className="profile-complete-avatar-block">
          <div className="profile-complete-avatar-wrap">
            <button
              type="button"
              className="profile-complete-avatar-circle"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              <EasyAvatar
                shape="circle"
                size={88}
                url={displayAvatarUrl}
                word={letter}
                alt=""
                className="profile-complete-avatar-large"
              />
              {!uploading ? (
                <span className="profile-complete-avatar-camera" aria-hidden>
                  <CameraOutlined />
                </span>
              ) : null}
            </button>
            {uploading ? (
              <div className="profile-complete-avatar-overlay" aria-hidden>
                处理中
              </div>
            ) : null}
          </div>
          {userId ? (
            <div className="profile-complete-user-id-row">
              <Tooltip title="您的用户ID">
                <span className="profile-complete-user-id-value" translate="no">
                  {userId}
                </span>
              </Tooltip>
              <button
                type="button"
                className="profile-complete-user-id-copy"
                onClick={onCopyUserId}
                aria-label="复制用户ID"
                title="复制"
              >
                <CopyOutlined />
              </button>
            </div>
          ) : null}
        </div>
        <div className="profile-complete-field">
          <Input
            className="profile-complete-input"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            maxLength={USER_PROFILE_NICKNAME_MAX_LENGTH}
            placeholder="昵称"
            showCount
            autoFocus
          />
        </div>
        <div className="profile-complete-field profile-complete-field--row">
          <div className="profile-complete-gender">
            <Radio.Group
              className="profile-complete-radio"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              optionType="button"
            >
              <Radio.Button value="U" title="未选">
                <span className="profile-complete-gender-inner">
                  <UserOutlined className="profile-complete-gi" />
                  未选
                </span>
              </Radio.Button>
              <Radio.Button value="M" title="男">
                <span className="profile-complete-gender-inner">
                  <ManOutlined className="profile-complete-gi" />
                  男
                </span>
              </Radio.Button>
              <Radio.Button value="F" title="女">
                <span className="profile-complete-gender-inner">
                  <WomanOutlined className="profile-complete-gi" />
                  女
                </span>
              </Radio.Button>
            </Radio.Group>
          </div>
        </div>
        <div className="profile-complete-field">
          <DatePicker
            className="profile-complete-input profile-complete-datepicker"
            value={birthday}
            onChange={(d) => setBirthday(d)}
            allowClear
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            defaultPickerValue={BIRTHDAY_PANEL_DEFAULT}
            placeholder="公历生日，选填"
            disabledDate={(d) =>
              d
                ? d.isAfter(dayjs(), "day") ||
                  d.isBefore(dayjs("1900-01-01"), "day")
                : false
            }
          />
        </div>
        <div
          className={
            allowDismiss
              ? "profile-complete-foot profile-complete-foot--split"
              : "profile-complete-foot"
          }
        >
          {allowDismiss ? (
            <Button
              className="profile-complete-cancel"
              disabled={submitting}
              onClick={() => {
                runInAction(() => {
                  globalModel.closeProfileEditor();
                });
              }}
            >
              取消
            </Button>
          ) : null}
          <Button
            type="primary"
            className="profile-complete-ok"
            loading={submitting}
            disabled={uploading}
            block={!allowDismiss}
            onClick={onSubmit}
          >
            确定
          </Button>
        </div>
      </div>
    </Modal>
  );
});
